/**
 * MapTiler configuration for the OpenSeaMap chart provider's base map.
 *
 * The OpenSeaMap provider (a base map with the seamark overlay on top) needs a
 * plain raster base. We use MapTiler's rasterised map tiles for that.
 *
 * KEY HANDLING: this is a CLIENT-SIDE map-tile key, committed to this PRIVATE
 * repo the same way the AISStream and Admiralty keys are. That's fine here. For
 * any PUBLIC release we'd do what we'd do with those: restrict the key to our
 * app's origins/referrers in the MapTiler dashboard, or relay tiles through our
 * own endpoint, so a committed key can't be lifted and run up a bill. Do NOT copy
 * this key into anything that ends up in a public repo.
 */

/** MapTiler Cloud API key (client-side map tiles; restrict/relay for public release). */
export const MAPTILER_KEY = 'kVgDn7yAsEYIAnK6ztEa';

/**
 * The MapTiler map style used as the base. One easy-to-change constant. `basic-v2`
 * is a clean, light style that reads well UNDER the seamark overlay (the marks
 * stand out rather than fighting a busy base). Alternatives worth trying on-device
 * if we want more coastal detail: `topo-v2`, `outdoor-v2`, or MapTiler's `ocean`.
 */
export const MAPTILER_BASE_STYLE = 'basic-v2';

/** Base map's zoom ceiling; the raster overzooms cleanly beyond this. */
export const MAPTILER_BASE_MAXZOOM = 20;

/** Credit required for the MapTiler base (MapTiler + OSM data). */
export const MAPTILER_ATTRIBUTION = '© MapTiler © OpenStreetMap contributors';

/**
 * Rasterised XYZ tile URL for a MapTiler map style, key already substituted.
 * Form (MapTiler Maps API): /maps/{style}/{tileSize}/{z}/{x}/{y}.png?key=…
 */
export function maptilerRasterTiles(style: string = MAPTILER_BASE_STYLE): string {
  return `https://api.maptiler.com/maps/${style}/256/{z}/{x}/{y}.png?key=${MAPTILER_KEY}`;
}

/**
 * MapTiler's OpenMapTiles VECTOR tile source (schema v3). Vector tiles are small,
 * GPU-rendered and sharp at any zoom, so the OpenSeaMap base draws from these
 * rather than streaming raster PNGs — far lighter on bandwidth and the main thread,
 * which is what the smooth modern chart apps use. We author a small light set of
 * land/water/road layers over this source (see map/vectorBasemap.ts) so the base
 * reads cleanly UNDER the seamark overlay, no vendor sprite/glyphs needed.
 */
export function maptilerVectorTiles(): string {
  return `https://api.maptiler.com/tiles/v3/{z}/{x}/{y}.pbf?key=${MAPTILER_KEY}`;
}

/** OpenMapTiles v3 vector tiles top out at z14; MapLibre overzooms cleanly beyond. */
export const MAPTILER_VECTOR_MAXZOOM = 14;

/**
 * MapTiler glyph (font) endpoint, key already substituted. Used with map.setGlyphs
 * so the OpenSeaMap vector base can show place/road labels. Full unicode coverage
 * and serves the same Open Sans our chart labels use, so switching the map to these
 * glyphs (from the limited MapLibre demo font server) is an upgrade, not a risk.
 * Form (MapTiler Fonts API): /fonts/{fontstack}/{range}.pbf?key=…
 */
export function maptilerGlyphsUrl(): string {
  return `https://api.maptiler.com/fonts/{fontstack}/{range}.pbf?key=${MAPTILER_KEY}`;
}
